import React from 'react';
import _ from 'lodash';
import Moment from 'moment';
import ReactDropDownCalender from '../ReactDropDownCalender';
import DropdownSelector from '../dropdowns/DropdownSelector';
import { FieldContractorUserService } from '../../services/FieldContractorUsers';
import { FieldTreeService } from '../../services/FieldTrees';
import { FieldGroupService } from '../../services/FieldGroups';
import { FieldItemService } from '../../services/FieldItems';
import FieldItemInputsService from '../../services/FieldItemInputs';
import { SafetyDiaryService } from '../../services/SafetyDiaries';
import LocalStorageService from '../../services/utilities/LocalStorageService';
import {
  DISPLAY_FLAG, // 表示フラグ
  FIELD_ITEM_LISTS,
  FLAG_STAUS,
  SAFETY_TYPE,
  REQUEST_PARAMS,
  USER_STATUS,
  SAFETY_DIARY_IMAGE_MAX_SIZE,
  USER_BOX_STATUS,
  DIRECT_STATUS_CODE,
} from '../../utilities/constants';
import SafetyDiariesConstants from '../../utilities/safetyDiariesConstants';
import ImageUtility from '../../utilities/imageUtility';
import { isPrimaryCompanyRoleId } from '../../utilities/roleUtility';
import { isApiFlagOn } from '../../utilities/commonUtil';
import { isNullOrEmpty, deleteControlCode } from '../../utilities/stringUtil';
import {
  FormElement,
  FormList,
  FormListItem,
  FormListItemChild,
  FormListItemGroup,
} from '../CommonFormComponents';
import { ArrowDownIcon } from '../icons/ArrowDownIcon';
import { RadioButtonIcon } from '../icons/RadioButtonIcon';
import { TrashIcon } from '../icons/TrashIcon';
import FieldService from '../../services/Fields';
import SessionStorageService from '../../services/utilities/SessionStorageService';

// 最大ファイル数
const maxImageCount = 4;

/**
 * 2019/12/04(Wed) @sa-yamamoto
 * この数値は連番である必要がなくユニークな文字列でよい
 * (連番だと意味があるのかと誤解させるので、ユニークな文字列の方がよい)
 * また、FIELD_A_ITEM ～ FIELD_D_ITEM は
 * 表示非表示に使われていないので
 * fieldNames と schemas において不要だが
 * リファクタリングしてデグレるリスクがあるので修正しないでおく
 * SafetyDiaryConfirmForm.jsx にも同様処理ありだが精査していない
 */
const fieldNames = {
  IMAGE: 0,
  REPORT_DATE: 1,
  REPORTER: 2,
  REPORT_DESCRIPTION: 3,
  REPORT_TYPE: 4,
  REPORT_LIMIT: 5,
  PRIORITY: 6,
  FIELD_ITEM: 7,
  FIELD_A_ITEM: 8,
  FIELD_B_ITEM: 9,
  FIELD_C_ITEM: 10,
  FIELD_D_ITEM: 11,
  PATROL: 12,
  NOTIFICATION: 13,
  ADDRESS: 14,
  SAFETY_DIARY: 15,
  NOTIFICATION_TGT: 16,
  NOTIFICATION_MAIL: 17,
  NOTIFICATION_LW: 18,
};

// Form schema
const schemas = {
  pointed_out: [
    fieldNames.PATROL,
    fieldNames.PRIORITY,
    fieldNames.REPORT_DESCRIPTION,
    fieldNames.FIELD_ITEM,
    fieldNames.FIELD_A_ITEM,
    fieldNames.FIELD_B_ITEM,
    fieldNames.FIELD_C_ITEM,
    fieldNames.FIELD_D_ITEM,
    fieldNames.ADDRESS,
    fieldNames.NOTIFICATION,
    fieldNames.NOTIFICATION_TGT,
    fieldNames.NOTIFICATION_MAIL,
    fieldNames.NOTIFICATION_LW,
    fieldNames.SAFETY_DIARY,
  ],
  inform: [
    fieldNames.PATROL,
    fieldNames.REPORT_LIMIT,
    fieldNames.PRIORITY,
    fieldNames.REPORT_DESCRIPTION,
    fieldNames.FIELD_ITEM,
    fieldNames.FIELD_A_ITEM,
    fieldNames.FIELD_B_ITEM,
    fieldNames.FIELD_C_ITEM,
    fieldNames.FIELD_D_ITEM,
    fieldNames.ADDRESS,
    fieldNames.NOTIFICATION,
    fieldNames.NOTIFICATION_MAIL,
    fieldNames.NOTIFICATION_LW,
    fieldNames.SAFETY_DIARY,
  ],
  others: [
    fieldNames.PATROL,
    fieldNames.PRIORITY,
    fieldNames.REPORT_DESCRIPTION,
    fieldNames.FIELD_ITEM,
    fieldNames.FIELD_A_ITEM,
    fieldNames.FIELD_B_ITEM,
    fieldNames.FIELD_C_ITEM,
    fieldNames.FIELD_D_ITEM,
    fieldNames.ADDRESS,
    fieldNames.SAFETY_DIARY,
  ],
  work_communication: [
    fieldNames.PATROL,
    fieldNames.PRIORITY,
    fieldNames.REPORT_DESCRIPTION,
    fieldNames.FIELD_ITEM,
    fieldNames.FIELD_A_ITEM,
    fieldNames.FIELD_B_ITEM,
    fieldNames.FIELD_C_ITEM,
    fieldNames.FIELD_D_ITEM,
    fieldNames.ADDRESS,
    fieldNames.NOTIFICATION,
    fieldNames.NOTIFICATION_MAIL,
    fieldNames.NOTIFICATION_LW,
    fieldNames.SAFETY_DIARY,
  ],
  other_communication: [
    fieldNames.PATROL,
    fieldNames.PRIORITY,
    fieldNames.REPORT_DESCRIPTION,
    fieldNames.FIELD_ITEM,
    fieldNames.FIELD_A_ITEM,
    fieldNames.FIELD_B_ITEM,
    fieldNames.FIELD_C_ITEM,
    fieldNames.FIELD_D_ITEM,
    fieldNames.ADDRESS,
    fieldNames.SAFETY_DIARY,
  ],
};

const notificationList = [
  'notification_manager',
  'notification_field_tree',
  'notification_group',
];

// 巡回記録新規登録/巡回記録詳細・編集画面用フォームデータ
export const SafetyDiaryFormData = {
  journal_type: '', // 記入者
  entries_date: Moment().format('YYYYMMDD'), // 記入日
  patrol: '', // 巡回パトロール
  type: SafetyDiariesConstants.reportType.pointOut.id, // 種別
  urgency: SafetyDiariesConstants.urgencyType.low.id, // 緊急度
  a_item_id: '', // 作業場所ID1
  b_item_id: '', // 作業場所ID2
  c_item_id: '', // 作業場所ID3
  d_item_id: '', // 作業場所ID4
  area_detail: '', // 詳細場所
  pointing_out_detail: '', // 内容
  image1: '', // 画像1
  image2: '', // 画像2
  image3: '', // 画像3
  image4: '', // 画像4
  notifications_company_flag: SafetyDiariesConstants.noticeFlag.off.id, // 通知対象_協力会社
  notifications_manager_flag: SafetyDiariesConstants.noticeFlag.off.id, // 通知対象_現場監督
  notifications_group_flag: SafetyDiariesConstants.noticeFlag.off.id, // 通知対象_グループ
  send_mail: 0, // 通知設定
  print_diary_flag: SafetyDiariesConstants.printDiaryFlag.off.id, // 安全衛生日誌
  end_date: Moment().format('YYYY/MM/DD'), //周知期限
  notification_field_tree: [],
  isMultiFileSizeError: false, // 複数ファイル_サイズチェック用フラグ
  send_external_service_cooperation: 0,
  lw_talkroom_ids: [],
  direct_talkroom_ids: [],
};

/**
 * @description 巡回記録新規登録/巡回記録一括登録/巡回記録詳細・編集フォーム
 */
export class SafetyDiaryForm extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      fieldId: SessionStorageService.getFieldId(), // 現場ID
      roleId: LocalStorageService.getRoleId(), // ロールID
      uploadImages: [], // アップロード画像
      formData: SafetyDiaryFormData, // フォームデータ
      isBulk: props.isBulk || false, // 一括登録か
      bulkId: props.bulkId || 0, // 親からフォームを閉じる時にフォームを特定するためのId
      notification_manager: [], // 通知対象 (現場監督)
      notification_manager_init_options: [], // 通知対象 (現場監督)
      notification_field_tree: [], // 通知対象 (協力会社のfield_tree_id)
      notification_company_init_options: [], // 通知対象 (協力会社)
      notification_group: [], // 通知対象 (グループ))
      notification_group_init_options: [], // 通知対象 (グループ))
      itemLabelNames: { A: '', B: '', C: '', D: '' }, // 作業場所ラベル名
      defaultItemNames: { A: '', B: '', C: '', D: '' }, // 作業場所初期表示名
      options: {}, // プルダウン初期表示用データ,
    };

    this.loaded = false;

    this.handleRemoveImage = this.handleRemoveImage.bind(this);
    this.handleTypeChange = this.handleTypeChange.bind(this);
    this.handleInput = this.handleInput.bind(this);
    this.handleToggleChange = this.handleToggleChange.bind(this);
    this.fileInput = React.createRef();
    this.fields = [
      { name: fieldNames.IMAGE, field: this.renderPatrolImageField },
      { name: fieldNames.REPORT_DATE, field: this.renderReportDateField },
      { name: fieldNames.REPORTER, field: this.renderReporterField },
      {
        name: fieldNames.REPORT_DESCRIPTION,
        field: this.renderReportDescrtiptionField,
      },
      { name: fieldNames.REPORT_TYPE, field: this.renderReportTypeField },
      { name: fieldNames.REPORT_LIMIT, field: this.renderReportLimitField },
      { name: fieldNames.PRIORITY, field: this.renderUrgencyField },
      { name: fieldNames.PATROL, field: this.renderPatrolField },
      { name: fieldNames.FIELD_ITEM, field: this.renderFieldItem },
      { name: fieldNames.FIELD_A_ITEM, field: this.renderFieldAItem },
      { name: fieldNames.FIELD_B_ITEM, field: this.renderFieldBItem },
      { name: fieldNames.FIELD_C_ITEM, field: this.renderFieldCItem },
      { name: fieldNames.FIELD_D_ITEM, field: this.renderFieldDItem },
      { name: fieldNames.SAFETY_DIARY, field: this.renderSafetyDiaryField },
      { name: fieldNames.NOTIFICATION, field: this.renderNotificationField },
      {
        name: fieldNames.NOTIFICATION_TGT,
        field: this.renderAdvancedNotificationField,
      },
      {
        name: fieldNames.NOTIFICATION_MAIL,
        field: this.renderNotificationMailField,
      },
      {
        name: fieldNames.NOTIFICATION_LW,
        field: this.renderNotificationLwField,
      },
    ];
  }

  componentDidMount() {
    // 一括登録の時はアップロードボタンのイベントをハンドリングしない
    if (!this.state.isBulk) {
      this.fileInput.current.addEventListener('change', event => {
        this.handleFileSelected(event.target.files[0]);
        event.target.value = '';
      });
    }

    FieldItemInputsService.itemNames(this.state.fieldId).then(itemNames =>
      this.setState({ itemLabelNames: itemNames })
    );

    // 更新時
    if (!_.isNull(this.props.dataId) && !this.state.isBulk) {
      this.loadDiary(this.props.dataId);
    }

    if (this.state.isBulk) {
      this.setState({ uploadImages: this.props.uploadImages });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.dataId !== this.props.dataId) {
      this.loadDiary(this.props.dataId);
    }
    if (prevState.uploadImages !== this.state.uploadImages) {
      // this.state.uploadImagesが更新されたらフォーム用のデータを更新する
      this.updateImagesInFormData();
    }
    if (prevState.formData !== this.state.formData) {
      this.props.onUpdate(this.state.formData);
    }
  }

  /**
   * @description 初期画像設定
   */
  processInitialImage(formData) {
    if (!formData) {
      return [];
    }
    let defaultUploadedImage = [];
    for (let i = 0; i < maxImageCount; i++) {
      if (!isNullOrEmpty(formData[`image${i + 1}`]) && formData[`image${i + 1}`]) {
        defaultUploadedImage.push({
          id: i,
          file: formData[`image${i + 1}`],
          url: formData[`image_url${i + 1}`],
        });
      }
    }
    return defaultUploadedImage;
  }

  /**
   * @description 巡回記録情報取得(1レコード)
   */
  loadDiary = dataId => {
    const params = this.props.params || {
      date_type: SafetyDiariesConstants.dataType.entriesDate,
    };
    SafetyDiaryService.getDiary(this.state.fieldId, dataId, params).then(
      response => {
        if (response.result === true) {
          const safetyDiary = response.data.entities.safety_diary;
          let notification_manager_init_options = [],
            notification_company_init_options = [],
            notification_group_init_options = [];
          // 取得したidをStringに変換 初期表示用のnameを取得
          _.map(safetyDiary.notification_manager, (value, key) => {
            safetyDiary.notification_manager[key] = String(value.id);
            notification_manager_init_options.push({user_id: value.id, user_name: value.name});
          });
          _.map(safetyDiary.notification_field_tree, (value, key) => {
            safetyDiary.notification_field_tree[key] = String(value.id);
            notification_company_init_options.push({ ...value, field_tree_id: value.id});
          });
          _.map(safetyDiary.notification_group, (value, key) => {
            safetyDiary.notification_group[key] = String(value.id);
            notification_group_init_options.push(value);
          });

          // V1データへの配慮
          if (
            safetyDiary.notifications_manager_flag === null &&
            safetyDiary.type === SAFETY_TYPE.point_out.id &&
            safetyDiary.notification_manager &&
            safetyDiary.notification_manager.length > 0
          ) {
            safetyDiary.notifications_manager_flag = FLAG_STAUS.positive.id;
          }
          if (
            safetyDiary.notifications_company_flag === null &&
            safetyDiary.type === SAFETY_TYPE.point_out.id &&
            safetyDiary.notification_field_tree &&
            safetyDiary.notification_field_tree.length > 0
          ) {
            safetyDiary.notifications_company_flag = FLAG_STAUS.positive.id;
          }
          if (
            safetyDiary.notifications_group_flag === null &&
            safetyDiary.type === SAFETY_TYPE.point_out.id &&
            safetyDiary.notification_group &&
            safetyDiary.notification_group.length > 0
          ) {
            safetyDiary.notifications_group_flag = FLAG_STAUS.positive.id;
          }

          const defaultItemNames = {
            A: safetyDiary.a_item_name,
            B: safetyDiary.b_item_name,
            C: safetyDiary.c_item_name,
            D: safetyDiary.d_item_name,
          };

          const uploadImages = this.processInitialImage(safetyDiary);
          this.setState({
            formData: safetyDiary,
            uploadImages,
            notification_manager_init_options,
            notification_company_init_options,
            notification_group_init_options,
            defaultItemNames,
          });

          if (_.isFunction(this.props.onLoaded)) {
            this.props.onLoaded(safetyDiary);
          }
        }
      }
    );
  };

  /**
   * @description 画像処理
   */
  async handleFileSelected(file) {
    if (this.state.uploadImages.length >= maxImageCount) {
      return;
    }

    const { maxFileSize } = SafetyDiariesConstants;
    const uploadFileCount = this.state.uploadImages.length + 1;
    const useMaxfileSize =
      uploadFileCount === 1 ? maxFileSize.single : maxFileSize.multi;
    const isValidType = await ImageUtility.fileTypeCheck(file);
    const isValidSize = await ImageUtility.dynamicFileSizeCheck(
      file,
      useMaxfileSize
    );

    if (!isValidType) {
      this.props.onVisibleDialog('errorfileType');
      return;
    }
    if (!isValidSize || this.state.isMultiFileSizeError) {
      this.props.onVisibleDialog('errorfileSize');
      return;
    }
    // 複数ファイルを設定した場合、1枚目がエラーにならないかを事前にチェック
    if (isValidSize && uploadFileCount === 1) {
      this.setState({ isMultiFileSizeError: file.size > maxFileSize.multi });
    }

    const reader = new FileReader();
    reader.onload = async event => {
      const currentFiles = this.state.uploadImages;
      const dataUrl = await ImageUtility.resizeImageBySize(
        event.target.result,
        SAFETY_DIARY_IMAGE_MAX_SIZE,
        file.type
      );
      const renew = currentFiles.concat({
        id: this.state.uploadImages.length,
        file: dataUrl,
        url: '',
      });
      return this.setState({ uploadImages: renew });
    };
    reader.readAsDataURL(file);
  }

  handleToggleChange = event => {
    const { name, value: flag } = event.target;
    let toggle;
    let initOptions = {};
    let notificationData = {};
    if (flag === String(FLAG_STAUS.negative.id)) {
      toggle = FLAG_STAUS.positive.id;
    } else {
      toggle = FLAG_STAUS.negative.id;
      let errorFlag;
      switch (name) {
        case 'notifications_manager_flag':
          errorFlag = 'notification_manager';
          initOptions.notification_manager_init_options = [];
          notificationData.notification_manager = [];
          break;
        case 'notifications_company_flag':
          errorFlag = 'notification_field_tree';
          initOptions.notification_company_init_options = [];
          notificationData.notification_field_tree = [];
          break;
        case 'notifications_group_flag':
          errorFlag = 'notification_group';
          initOptions.notification_group_init_options = [];
          notificationData.notification_group = [];
          break;
        case 'send_external_service_cooperation':
          errorFlag = SafetyDiariesConstants.keyLwId;
          notificationData.lw_talkroom_ids = [];
          notificationData.direct_talkroom_ids = [];
          break;
        default:
          break;
      }
      if (errorFlag) {
        this.props.setValidateMessages(_.omit(this.props.errors, errorFlag));
      }
    }
    const formData = { ...this.state.formData, [name]: toggle, ...notificationData };
    this.setState({ formData, ...initOptions });
  };

  // 種別を変更するとフォーム内容を変更する
  handleTypeChange = event => {
    const { value } = event.target;
    const type = parseInt(value);
    let formData = _.cloneDeep(this.state.formData);

    if (
      type === SAFETY_TYPE.other.id ||
      type === SAFETY_TYPE.other_contact.id
    ) {
      formData = {
        ...formData,
        notifications_manager_flag: '',
        notifications_company_flag: '',
        notifications_group_flag: '',
        notification_manager: [],
        notification_field_tree: [],
        notification_group: [],
        send_mail: 0,
        type,
        send_external_service_cooperation: 0,
        lw_talkroom_ids: [],
        direct_talkroom_ids: [],
      };
    } else {
      formData = {
        ...formData,
        notifications_manager_flag: 0,
        notifications_company_flag: 0,
        notifications_group_flag: 0,
        notification_manager: [],
        notification_field_tree: [],
        notification_group: [],
        send_mail: 0,
        type,
        send_external_service_cooperation: 0,
        lw_talkroom_ids: [],
        direct_talkroom_ids: [],
      };
    }

    this.setState({
      formData,
      notification_manager_init_options: [],
      notification_company_init_options: [],
      notification_group_init_options: [],
    });
    this.props.setValidateMessages(
      _.omit(
        this.props.errors,
        notificationList,
        SafetyDiariesConstants.keyLwId
      )
    );
  };

  // フォーム入力対応
  handleInput = event => {
    const { name, value } = event.target;
    const formData = {
      ...this.state.formData,
      [name]: deleteControlCode(value),
    };
    this.setState({ formData });
  };

  /**
   * @description ドロップダウン項目設定
   * @param {string} name フォーム名
   * @param {string} value 選択値
   * @param {boolean} triggerValidate
   */
  handleSelectorChange = (name, value, triggerValidate) => {
    let newValue = value;
    if (triggerValidate && !value) {
      newValue = this.state.formData[name];
    }
    if (_.isArray(value) && notificationList.includes(name)) {
      newValue = _.map(value, val => String(val));
    }
    const formData = { ...this.state.formData, [name]: newValue };
    this.setState({ formData }, () => {
      if (triggerValidate) {
        if (!this.state.isBulk) {
          this.props.onValidate(formData, false, name);
        } else {
          this.props.onValidate(formData, this.state.bulkId, name);
        }
      }
    });
  };

  /**
   * @description 記入日、周知期限設定
   * @param {string} id フォームID
   * @param {string} date 設定日付
   */
  handleCalenderChange = (id, date) => {
    date = date.split(/\//).join('');
    const renew = _.assign({}, { ...this.state.formData }, { [id]: date });
    if (id === 'entries_date') {
      renew.end_date = date;
    }

    if (this.state.isBulk) {
      this.props.onValidate(renew, this.state.bulkId);
    } else {
      this.props.onValidate(renew);
    }

    this.setState({ formData: renew });
  };

  /**
   * @description 画像削除
   * @param {*} e
   * @param {int} imageId 画像ID
   */
  handleRemoveImage = (e, imageId) => {
    const uploadImages = _.cloneDeep(this.state.uploadImages);
    // recalc index
    const newImages = _.map(
      _.remove(uploadImages, image => image.id !== imageId),
      (image, i) => ({ url: image.url, file: image.file, id: i })
    );
    if (imageId === 0) {
      this.setState({ isMultiFileSizeError: false });
    }
    this.setState({ uploadImages: newImages });
  };

  /**
   * @description 画像削除(巡回記録一括登録)
   * @param {int} bulkId 一括登録ID
   */
  handleDeleteForm = bulkId => {
    this.props.onDeleteForm(bulkId);
  };

  /**
   * @description 画像登録
   */
  updateImagesInFormData() {
    if (this.state.uploadImages) {
      const prefix = 'image';
      const uploadImages = _.cloneDeep(this.state.uploadImages);
      let renew = _.cloneDeep(this.state.formData);
      for (let i = 0; i < maxImageCount; i++) {
        renew = {
          ...renew,
          [prefix.concat(i + 1)]: uploadImages[i] ? uploadImages[i].file : '',
        };
      }
      this.setState({ formData: renew });
    }
  }

  filteredItemList = (response, dataKey) => {
    const r = _.map(response.data.entities[dataKey], item => ({
      id: item.id,
      name: item.name,
    }));
    return {
      data: r,
      total: r.length,
    };
  };

  /**
   * @description 画像表示
   * @param {object} image 画像情報
   */
  renderPatrolImage = (image, index) => {
    return (
      <div key={index} className='img-patrol'>
        <div className='img-patrol__inner'>
          <img
            src={image.url === '' ? image.file : image.url}
            alt={`${image.id}`}
          />
        </div>
        {!this.state.isBulk && (
          <button
            id={image.id}
            className='btn-icon form-list__element--btn_delete'
            onClick={e => this.handleRemoveImage(e, image.id)}
          >
            <TrashIcon className='icon--2_4x form-list__trash-icon' />
          </button>
        )}
      </div>
    );
  };

  /**
   * @description 追加するボタン表示
   */
  renderAddPatrolImageButton = () => {
    const uid = _.uniqueId;
    return (
      <div
        className='patrol-image__footer'
        style={
          this.state.uploadImages.length >= maxImageCount
            ? { display: 'none' }
            : null
        }
      >
        <label htmlFor={`upload-${uid}`} className='btn--sub'>
          画像を追加する
        </label>
        <input
          id={`upload-${uid}`}
          name={`upload-${uid}`}
          type='file'
          ref={this.fileInput}
          style={{ display: 'none' }}
          accept='image/png,image/jpeg,image/gif'
        />
        <span className='patrol-image__footer-info'>{`※最大${maxImageCount}枚まで`}</span>
      </div>
    );
  };

  /**
   * @description この写真を削除ボタン表示
   */
  renderDeleteFormButton = () => {
    return (
      <button
        className='patrol-image__header-btn-delete btn--negative'
        onClick={() => this.handleDeleteForm(this.state.bulkId)}
      >
        この画像を削除
      </button>
    );
  };

  /**
   * @description 画像フォーム
   */
  renderPatrolImageField = () => {
    return (
      <div className='patrol-image'>
        <div className='patrol-image__header'>
          {this.state.isBulk && this.renderDeleteFormButton()}
        </div>
        {!_.isEmpty(this.state.uploadImages) && (
          <div className='patrol-image__image'>
            {_.map(this.state.uploadImages, (image, index) => {
              return this.renderPatrolImage(image, index);
            })}
          </div>
        )}
        {!this.state.isBulk && this.renderAddPatrolImageButton()}
      </div>
    );
  };

  /**
   * 記入日
   */
  renderReportDateField = () => {
    return (
      <FormListItem label='記入日'>
        <FormElement>
          <ReactDropDownCalender
            onChange={date => this.handleCalenderChange('entries_date', date)}
            value={this.state.formData.entries_date}
          />
        </FormElement>
      </FormListItem>
    );
  };

  /**
   * @description 記入者オプション設定
   */
  getReporterFieldOptions = () => {
    const formJournalType = this.state.formData.journal_type;
    const { journalType, foremanJournalType } = SafetyDiariesConstants;

    if (isPrimaryCompanyRoleId(this.state.roleId)) {
      if (!_.isNull(this.props.dataId)) {
        switch (formJournalType) {
          case foremanJournalType.safetyForeman.id:
            return {
              ...journalType,
              safetyForeman: foremanJournalType.safetyForeman,
            };
          case foremanJournalType.otherForeman.id:
            return {
              ...journalType,
              otherForeman: foremanJournalType.otherForeman,
            };
          default:
            return journalType;
        }
      } else {
        return journalType;
      }
    } else {
      return foremanJournalType;
    }
  };

  /**
   * 記入者
   */
  renderReporterField = () => {
    return (
      <FormListItem label='記入者'>
        <FormElement>
          <DropdownSelector
            label='記入者を選択'
            options={this.getReporterFieldOptions()}
            selectorName='journal_type'
            selectedValues={
              this.state.formData.journal_type
                ? [this.state.formData.journal_type]
                : []
            }
            multiple={false}
            onSelect={(name, values) => {
              this.handleSelectorChange(name, values);
            }}
            isOptional={true}
            dropdownSelectStyle={{ width: '238px' }}
          />
        </FormElement>
      </FormListItem>
    );
  };

  /**
   * 種別
   */
  renderReportTypeField = () => {
    return (
      <FormListItem label='種別'>
        <FormElement>
          <select
            name='type'
            id='type'
            onChange={this.handleTypeChange}
            value={this.state.formData.type}
          >
            <option value={SafetyDiariesConstants.reportType.pointOut.id}>
              指摘(巡回)
            </option>
            <option value={SafetyDiariesConstants.reportType.wellKnown.id}>
              周知(巡回)
            </option>
            <option value={SafetyDiariesConstants.reportType.other.id}>
              その他(巡回)
            </option>
            <option value={SafetyDiariesConstants.reportType.contact.id}>
              当日作業連絡
            </option>
            <option value={SafetyDiariesConstants.reportType.otherContact.id}>
              その他連絡
            </option>
          </select>
          <ArrowDownIcon className='icon--2_4x form-list__arrow-icon' />
        </FormElement>
      </FormListItem>
    );
  };

  /**
   * 巡回パトロール
   */
  renderPatrolField = () => {
    return (
      <FormListItem label='巡回パトロール'>
        <FormElement>
          <input
            name='patrol'
            type='text'
            maxLength='50'
            value={this.state.formData.patrol}
            onChange={this.handleInput}
          />
        </FormElement>
      </FormListItem>
    );
  };

  renderReportLimitField = () => {
    return (
      <FormListItem label='周知期限'>
        <FormElement validate={_.get(this.props.errors, 'end_date')}>
          <ReactDropDownCalender
            onChange={date => this.handleCalenderChange('end_date', date)}
            value={this.state.formData.end_date}
          />
        </FormElement>
      </FormListItem>
    );
  };

  renderUrgencyField = () => {
    const { urgencyType } = SafetyDiariesConstants;
    return (
      <FormListItem label='緊急度'>
        {/* SafetyDiariesBulkRegisterでこのコンポーネントが複数並ぶ場合にradioのnameが重複することで
            クリックしたときにONにならない場合がある。formタグを付けることで回避する。 */}
        <FormElement>
          <form className='form-list-radio'>
            {_.map(urgencyType, urgency => (
              <div className='form-list-radio__item' key={urgency.id}>
                <label className='radio'>
                  <input
                    type='radio'
                    name='urgency'
                    value={urgency.id}
                    checked={
                      Number(this.state.formData.urgency) === Number(urgency.id)
                    }
                    onChange={this.handleInput}
                  />
                  <RadioButtonIcon className='icon--2x' />
                  <span>{urgency.name}</span>
                </label>
              </div>
            ))}
          </form>
        </FormElement>
      </FormListItem>
    );
  };

  renderReportDescrtiptionField = () => {
    return (
      <FormListItem label='内容'>
        <FormElement>
          <textarea
            name='pointing_out_detail'
            maxLength='1024'
            value={this.state.formData.pointing_out_detail}
            onChange={this.handleInput}
          />
        </FormElement>
      </FormListItem>
    );
  };

  renderFieldItem = () => {
    const item_list_ids = [1, 2, 3, 4];
    _.map(item_list_ids, (itemListId, key) => {
      if (this.props.value[`field_item_list${itemListId}_display_flag`] === 0) {
        item_list_ids.splice(key, 1);
      }
    });
    if (item_list_ids.length > 0) {
      if (this.loaded === false) {
        this.loaded = true;
        const params = {
          field_id: this.state.fieldId,
          item_list_ids,
          is_limit: 1,
          offset: 0,
          limit: REQUEST_PARAMS.parentLimit,
          display_flag: DISPLAY_FLAG.use.id,
        };
        // 現場作業エリア情報取得API
        FieldItemService.list(this.state.fieldId, params).then(response => {
          if (response.result === false) {
            return;
          }
          this.setState({ options: response.data.entities });
        });
      }
      return (
        <FormListItem label='場所'>
          <div>
            <FormElement>
              {this.renderFieldAItem(this.state.options, this.loaded)}
            </FormElement>
            <FormElement>
              {this.renderFieldBItem(this.state.options, this.loaded)}
            </FormElement>
            <FormElement>
              {this.renderFieldCItem(this.state.options, this.loaded)}
            </FormElement>
            <FormElement>
              {this.renderFieldDItem(this.state.options, this.loaded)}
            </FormElement>
            <FormElement>{this.renderAddressField()}</FormElement>
          </div>
        </FormListItem>
      );
    }
  };

  /**
   * Field A
   *
   * @memberof SafetyDiaryForm
   */
  renderFieldAItem = (options, loaded) => {
    if (!isApiFlagOn(this.props.value.field_item_list1_display_flag)) {
      return;
    }
    if (this.state.itemLabelNames.A === '') {
      return;
    }
    return (
      <div className='form-list__dropdown-item'>
        <DropdownSelector
          label={`${this.state.itemLabelNames.A}を選択`}
          multiple={false}
          selectorName='a_item_id'
          selectedName={this.state.defaultItemNames.A}
          api={params => FieldItemService.list(this.state.fieldId, params)}
          apiParams={{
            field_id: this.state.fieldId,
            item_list_ids: FIELD_ITEM_LISTS.a.id,
            limit: REQUEST_PARAMS.parentLimit,
            display_flag: DISPLAY_FLAG.use.id,
          }}
          apiSearchKey='name'
          selectedValues={
            this.state.formData.a_item_id ? [this.state.formData.a_item_id] : []
          }
          keyName='name'
          valueName='id'
          apiProcess={response =>
            this.filteredItemList(response, 'field_item_a_lists')
          }
          onSelect={(name, values) => {
            this.handleSelectorChange(name, values);
          }}
          options={options.field_item_a_lists}
          loaded={loaded}
          isOptional={true}
          dropdownSelectStyle={{ width: '238px' }}
        />
      </div>
    );
  };

  /**
   * Field B
   *
   * @memberof SafetyDiaryForm
   */
  renderFieldBItem = (options, loaded) => {
    if (!isApiFlagOn(this.props.value.field_item_list2_display_flag)) {
      return;
    }
    if (this.state.itemLabelNames.B === '') {
      return;
    }
    return (
      <div className='form-list__dropdown-item'>
        <DropdownSelector
          label={`${this.state.itemLabelNames.B}を選択`}
          multiple={false}
          selectorName='b_item_id'
          selectedName={this.state.defaultItemNames.B}
          api={params => FieldItemService.list(this.state.fieldId, params)}
          apiParams={{
            field_id: this.state.fieldId,
            item_list_ids: FIELD_ITEM_LISTS.b.id,
            limit: REQUEST_PARAMS.parentLimit,
            display_flag: DISPLAY_FLAG.use.id,
          }}
          apiSearchKey='name'
          selectedValues={
            this.state.formData.b_item_id ? [this.state.formData.b_item_id] : []
          }
          keyName='name'
          valueName='id'
          apiProcess={response =>
            this.filteredItemList(response, 'field_item_b_lists')
          }
          onSelect={(name, values) => {
            this.handleSelectorChange(name, values);
          }}
          options={options.field_item_b_lists}
          loaded={loaded}
          isOptional={true}
          dropdownSelectStyle={{ width: '238px' }}
        />
      </div>
    );
  };

  /**
   * Field C
   *
   * @memberof SafetyDiaryForm
   */
  renderFieldCItem = (options, loaded) => {
    if (!isApiFlagOn(this.props.value.field_item_list3_display_flag)) {
      return;
    }
    if (this.state.itemLabelNames.C === '') {
      return;
    }
    return (
      <div className='form-list__dropdown-item'>
        <DropdownSelector
          label={`${this.state.itemLabelNames.C}を選択`}
          multiple={false}
          selectorName='c_item_id'
          selectedName={this.state.defaultItemNames.C}
          api={params => FieldItemService.list(this.state.fieldId, params)}
          apiParams={{
            field_id: this.state.fieldId,
            item_list_ids: FIELD_ITEM_LISTS.c.id,
            limit: REQUEST_PARAMS.parentLimit,
            display_flag: DISPLAY_FLAG.use.id,
          }}
          apiSearchKey='name'
          selectedValues={
            this.state.formData.c_item_id ? [this.state.formData.c_item_id] : []
          }
          keyName='name'
          valueName='id'
          apiProcess={response =>
            this.filteredItemList(response, 'field_item_c_lists')
          }
          onSelect={(name, values) => {
            this.handleSelectorChange(name, values);
          }}
          options={options.field_item_c_lists}
          loaded={loaded}
          isOptional={true}
          dropdownSelectStyle={{ width: '238px' }}
        />
      </div>
    );
  };

  /**
   * Field D
   *
   * @memberof SafetyDiaryForm
   */
  renderFieldDItem = (options, loaded) => {
    if (!isApiFlagOn(this.props.value.field_item_list4_display_flag)) {
      return;
    }
    if (this.state.itemLabelNames.D === '') {
      return;
    }
    return (
      <div className='form-list__dropdown-item'>
        <DropdownSelector
          label={`${this.state.itemLabelNames.D}を選択`}
          multiple={false}
          selectorName='d_item_id'
          selectedName={this.state.defaultItemNames.D}
          api={params => FieldItemService.list(this.state.fieldId, params)}
          apiParams={{
            field_id: this.state.fieldId,
            item_list_ids: FIELD_ITEM_LISTS.d.id,
            limit: REQUEST_PARAMS.parentLimit,
            display_flag: DISPLAY_FLAG.use.id,
          }}
          apiSearchKey='name'
          selectedValues={
            this.state.formData.d_item_id ? [this.state.formData.d_item_id] : []
          }
          keyName='name'
          valueName='id'
          apiProcess={response =>
            this.filteredItemList(response, 'field_item_d_lists')
          }
          onSelect={(name, values) => {
            this.handleSelectorChange(name, values);
          }}
          options={options.field_item_d_lists}
          loaded={loaded}
          isOptional={true}
          dropdownSelectStyle={{ width: '238px' }}
        />
      </div>
    );
  };

  renderAddressField = () => {
    return (
      <input
        placeholder='補足事項を入力'
        name='area_detail'
        value={this.state.formData.area_detail}
        type='text'
        maxLength='512'
        onChange={this.handleInput}
        className='form-list__address-field'
      />
    );
  };

  renderNotificationField = () => {
    const uid = _.uniqueId();
    const checkManager =
      this.state.formData.notifications_manager_flag === FLAG_STAUS.positive.id;
    const checkCompany =
      this.state.formData.notifications_company_flag === FLAG_STAUS.positive.id;
    const checkGroup =
      this.state.formData.notifications_group_flag === FLAG_STAUS.positive.id;

    return (
      <FormListItem
        label='通知対象'
        isBorder={
          !(
            this.state.formData.type ===
              SafetyDiariesConstants.reportType.pointOut.id &&
            (checkManager || checkCompany || checkGroup)
          )
        }
      >
        <FormElement>
          <div className='form-container'>
            <div className='form-container__group'>
              <div className='form-container__item'>
                {isPrimaryCompanyRoleId(this.state.roleId) && (
                  <label htmlFor={`radio-2-101-1${uid}`} className='checkbox'>
                    <input
                      type='checkbox'
                      id={`radio-2-101-1${uid}`}
                      name='notifications_manager_flag'
                      value={this.state.formData.notifications_manager_flag}
                      checked={checkManager}
                      onChange={this.handleToggleChange}
                    />
                    <i className='i-square i-3x' aria-hidden='true' />
                    <span>現場監督</span>
                  </label>
                )}
                <label htmlFor={`radio-2-101-2${uid}`} className='checkbox'>
                  <input
                    type='checkbox'
                    id={`radio-2-101-2${uid}`}
                    name='notifications_company_flag'
                    value={this.state.formData.notifications_company_flag}
                    checked={checkCompany}
                    onChange={this.handleToggleChange}
                  />
                  <i className='i-square i-3x' aria-hidden='true' />
                  <span>協力会社</span>
                </label>
                {isPrimaryCompanyRoleId(this.state.roleId) && (
                  <label htmlFor={`radio-2-101-3${uid}`} className='checkbox'>
                    <input
                      type='checkbox'
                      id={`radio-2-101-3${uid}`}
                      name='notifications_group_flag'
                      value={this.state.formData.notifications_group_flag}
                      checked={checkGroup}
                      onChange={this.handleToggleChange}
                    />
                    <i className='i-square i-3x' aria-hidden='true' />
                    <span>グループ</span>
                  </label>
                )}
              </div>
            </div>
          </div>
        </FormElement>
      </FormListItem>
    );
  };

  /**
   * 通知対象フィールド
   *
   * @memberof SafetyDiaryForm
   */
  renderAdvancedNotificationField = () => {
    const checkManager =
      this.state.formData.notifications_manager_flag === FLAG_STAUS.positive.id;
    const checkCompany =
      this.state.formData.notifications_company_flag === FLAG_STAUS.positive.id;
    const checkGroup =
      this.state.formData.notifications_group_flag === FLAG_STAUS.positive.id;
    return (
      <React.Fragment>
        {this.state.formData.type ===
          SafetyDiariesConstants.reportType.pointOut.id &&
          (checkManager || checkCompany || checkGroup) &&
          this.renderNotificationTargetSelector()}
      </React.Fragment>
    );
  };

  /**
   * 通知設定フィールド
   *
   * @memberof SafetyDiaryForm
   */
  renderNotificationMailField = () => {
    const checkSendMail =
      this.state.formData.send_mail === FLAG_STAUS.positive.id;
    const uid = _.uniqueId();

    return (
      !this.props.isEdit && (
        <FormListItem label='メール通知設定'>
          <FormElement
            info={`※通知対象のユーザーに対し、登録内容をメールでもお知らせします。メール通知設定は新規登録時にのみ設定可能です。`}
          >
            <label htmlFor={`send_mail${uid}`} className='checkbox'>
              <input
                type='checkbox'
                id={`send_mail${uid}`}
                name='send_mail'
                value={this.state.formData.send_mail}
                checked={checkSendMail}
                onChange={this.handleToggleChange}
              />
              <i className='i-square i-3x' aria-hidden='true' />
              <span>メールで通知を送る</span>
            </label>
          </FormElement>
        </FormListItem>
      )
    );
  };

  renderNotificationTargetSelector = () => {
    return (
      <FormListItemGroup isBorder={false}>
        {this.state.formData.notifications_manager_flag ===
          FLAG_STAUS.positive.id && (
          <FormListItemChild label='現場監督' required={true}>
            {this.renderNotificationManagerField()}
          </FormListItemChild>
        )}
        {this.state.formData.notifications_company_flag ===
          FLAG_STAUS.positive.id && (
          <FormListItemChild label='協力会社' required={true}>
            {this.renderNotificationPartnerField()}
          </FormListItemChild>
        )}
        {this.state.formData.notifications_group_flag ===
          FLAG_STAUS.positive.id && (
          <FormListItemChild label='グループ' required={true}>
            {this.renderNotificationGroupField()}
          </FormListItemChild>
        )}
      </FormListItemGroup>
    );
  };

  /**
   * 通知対象: 現場監督
   *
   * @memberof SafetyDiaryForm
   */
  renderNotificationManagerField = () => {
    return (
      <FormElement validate={_.get(this.props.errors, 'notification_manager')}>
        <DropdownSelector
          label='現場監督を選択'
          multiple={true}
          hasTooltip={true}
          selectorName='notification_manager'
          selectedOptions={this.state.notification_manager_init_options}
          api={params =>
            FieldContractorUserService.list(this.state.fieldId, params)
          }
          apiParams={{
            block_flag: USER_STATUS.use.id,
            field_contractor_user_block_flag: USER_BOX_STATUS.active.value,
          }}
          apiSearchKey='name'
          selectedValues={this.state.formData.notification_manager}
          keyName='user_name'
          valueName='user_id'
          apiBindDataKey={'field_contractor_users'}
          onSelect={(name, values) => {
            this.handleSelectorChange(name, values, true);
          }}
          onClose={() => {
            this.handleSelectorChange('notification_manager', null, true);
          }}
          dropdownSelectStyle={{ width: '238px' }}
        />
      </FormElement>
    );
  };

  /**
   * 通知対象: 協力会社
   *
   * @memberof SafetyDiaryForm
   */
  renderNotificationPartnerField = () => {
    return (
      <FormElement
        validate={_.get(this.props.errors, 'notification_field_tree')}
      >
        <DropdownSelector
          label='協力会社を選択'
          multiple={true}
          hasTooltip={true}
          selectorName='notification_field_tree'
          selectedOptions={this.state.notification_company_init_options}
          api={params =>
            FieldTreeService.selectableCompaniesList(this.state.fieldId, params)
          }
          limit={REQUEST_PARAMS.parentLimit}
          apiParams={{
            field_id: this.state.fieldId,
            show_no_foreman: 1,
          }}
          apiSearchKey='name'
          selectedValues={this.state.formData.notification_field_tree}
          keyName='name'
          valueName='field_tree_id'
          apiBindDataKey={'companies'}
          onSelect={(name, values) => {
            this.handleSelectorChange(name, values, true);
          }}
          onClose={() => {
            this.handleSelectorChange('notification_field_tree', null, true);
          }}
          widthClass='dropdown__item--wide'
          exceptionAllLoadedLimit={true}
          dropdownSelectStyle={{ width: '238px' }}
        />
      </FormElement>
    );
  };

  /**
   * 検索用グループ選択の一覧の表示
   *
   * @memberof SafetyDiaryForm
   */
  renderNotificationGroupField = () => {
    return (
      <FormElement validate={_.get(this.props.errors, 'notification_group')}>
        <DropdownSelector
          label='グループを選択'
          multiple={true}
          hasTooltip={true}
          selectorName='notification_group'
          selectedOptions={this.state.notification_group_init_options}
          api={params =>
            // 検索用グループ一覧
            FieldGroupService.listForSearch(this.state.fieldId, params)
          }
          apiParams={{}}
          apiSearchKey='name'
          selectedValues={this.state.formData.notification_group}
          keyName='name'
          valueName='id'
          apiBindDataKey={'field_groups'}
          onSelect={(name, values) => {
            this.handleSelectorChange(name, values, true);
          }}
          onClose={() => {
            this.handleSelectorChange('notification_group', null, true);
          }}
          dropdownSelectStyle={{ width: '238px' }}
        />
      </FormElement>
    );
  };

  /**
   * 安全衛生日誌
   *
   * @memberof SafetyDiaryForm
   */
  renderSafetyDiaryField = () => {
    const uid = _.uniqueId();
    return (
      <FormListItem label='安全衛生日誌'>
        <FormElement>
          <label htmlFor={`checkbox-2-102${uid}`} className='checkbox'>
            <input
              type='checkbox'
              name='print_diary_flag'
              value={this.state.formData.print_diary_flag}
              onClick={this.handleToggleChange}
              checked={String(this.state.formData.print_diary_flag) === '1'}
              id={`checkbox-2-102${uid}`}
            />
            <i className='i-square i-3x' aria-hidden='true' />
            <span>反映する</span>
          </label>
        </FormElement>
      </FormListItem>
    );
  };

  renderNotificationLwField = () => {
    const uid = _.uniqueId();
    const {
      formData: { send_external_service_cooperation, type },
    } = this.state;
    const { line_works, direct } = this.props.externalServiceCooperationStatus;
    const isShowField =
      !_.includes([SAFETY_TYPE.other.id, SAFETY_TYPE.other_contact.id], type) &&
      !this.props.isEdit &&
      (line_works.available ||
        direct.available ||
        direct.reason_code === DIRECT_STATUS_CODE.UNAVAILABLE);
    return (
      isShowField && (
        <React.Fragment>
          <FormListItem
            label='チャット通知設定'
            isBorder={!send_external_service_cooperation}
          >
            <FormElement info='選択対象のトークルームに対して通知メッセージを送信します。'>
              <label htmlFor={`checkbox-2-102${uid}`} className='checkbox'>
                <input
                  type='checkbox'
                  name='send_external_service_cooperation'
                  value={send_external_service_cooperation}
                  checked={
                    Number(send_external_service_cooperation) ===
                    FLAG_STAUS.positive.id
                  }
                  onChange={this.handleToggleChange}
                  id={`checkbox-2-102${uid}`}
                />
                <i className='i-square i-3x' aria-hidden='true' />
                <span>連携チャットへ送信する</span>
              </label>
            </FormElement>
          </FormListItem>
          {!!send_external_service_cooperation && (
            <React.Fragment>
              {this.renderTalkRoomField()}
              {this.renderDirectRoomField()}
            </React.Fragment>
          )}
        </React.Fragment>
      )
    );
  };

  renderTalkRoomField = () => {
    const {
      line_works: { available },
    } = this.props.externalServiceCooperationStatus;
    return (
      available && (
        <FormListItemGroup isBorder={false}>
          <FormListItemChild
            label='LINE WORKS トークルーム'
            required={true}
            requiredText={this.getRequiredText()}
          >
            <FormElement validate={_.get(this.props.errors, 'lw_talkroom_ids')}>
              <DropdownSelector
                label='トークルームを選択'
                multiple={true}
                hasTooltip={true}
                selectorName='lw_talkroom_ids'
                api={() => FieldService.listLwTalkRoom(this.state.fieldId)}
                apiSearchKey=''
                selectedValues={this.state.formData.lw_talkroom_ids}
                keyName='name'
                valueName='id'
                apiBindDataKey={'lw_talkrooms'}
                widthClass='dropdown__item'
                onSelect={(name, values) => {
                  this.handleSelectorChange(name, values, true);
                }}
                onClose={() => {
                  this.handleSelectorChange('lw_talkroom_ids', null, true);
                }}
                dropdownSelectStyle={{ width: '238px' }}
              />
            </FormElement>
          </FormListItemChild>
        </FormListItemGroup>
      )
    );
  };

  renderDirectRoomField = () => {
    const {
      direct: { available, reason_code },
    } = this.props.externalServiceCooperationStatus;
    if (
      reason_code === DIRECT_STATUS_CODE.UNAVAILABLE ||
      (available && !this.props.callApiDirectTalkRoomStatus)
    ) {
      return (
        <FormListItemGroup isBorder={false}>
          <FormListItemChild
            label='direct トークルーム'
            required={true}
            requiredText={this.getRequiredText()}
          >
            <FormElement
              validate={_.get(this.props.errors, 'direct_talkroom_ids')}
            >
              現在directサービス連携は利用できません。時間をおいて再度お試しください。
            </FormElement>
          </FormListItemChild>
        </FormListItemGroup>
      );
    }
    return (
      <React.Fragment>
        {available && (
          <FormListItemGroup isBorder={false}>
            <FormListItemChild
              label='direct トークルーム'
              required={true}
              requiredText={this.getRequiredText()}
            >
              <FormElement
                validate={_.get(this.props.errors, 'direct_talkroom_ids')}
              >
                <DropdownSelector
                  label='トークルームを選択'
                  multiple={true}
                  hasTooltip={true}
                  selectorName='direct_talkroom_ids'
                  options={this.props.directTalkRooms}
                  apiSearchKey=''
                  selectedValues={this.state.formData.direct_talkroom_ids}
                  keyName='name'
                  valueName='talk_id'
                  apiBindDataKey={'direct_talkrooms'}
                  widthClass='dropdown__item'
                  onSelect={(name, values) => {
                    this.handleSelectorChange(name, values, true);
                  }}
                  onClose={() => {
                    this.handleSelectorChange(
                      'direct_talkroom_ids',
                      null,
                      true
                    );
                  }}
                  dropdownSelectStyle={{ width: '238px' }}
                />
              </FormElement>
            </FormListItemChild>
          </FormListItemGroup>
        )}
      </React.Fragment>
    );
  };

  getRequiredText = () => {
    const { line_works, direct } = this.props.externalServiceCooperationStatus;
    if (
      line_works.available &&
      (direct.available ||
        direct.reason_code === DIRECT_STATUS_CODE.UNAVAILABLE)
    ) {
      return 'どちらか必須';
    }
  };

  /**
   * this.fields に入っている値から name が一致するものを見つけ
   * fieldメソッドを呼び出す
   */
  renderField(name) {
    const f = _.find(this.fields, field => {
      return field.name === name;
    });
    if (!f) {
      return null;
    }
    return f.field();
  }

  /**
   * 指定された引数の type(レポートタイプ) に応じて
   * schemas で定義されているコントロール一覧を返す
   */
  schemaByReportType(type) {
    const { reportType } = SafetyDiariesConstants;
    switch (parseInt(type)) {
      case reportType.pointOut.id:
        return schemas.pointed_out;
      case reportType.wellKnown.id:
        return schemas.inform;
      case reportType.other.id:
        return schemas.others;
      case reportType.contact.id:
        return schemas.work_communication;
      case reportType.otherContact.id:
        return schemas.other_communication;
      default:
        return [];
    }
  }

  renderCreateUserField = () => {
    return (
      <FormListItem label='登録者'>
        <FormElement>{this.state.formData.create_user_name}</FormElement>
      </FormListItem>
    );
  };

  renderCreateTimeField = () => {
    return (
      <FormListItem label='登録日時'>
        <FormElement>
          {this.state.formData.created_at !== null &&
          this.state.formData.create_user_name
            ? Moment(this.state.formData.created_at).format(
                'YYYY/MM/DD(ddd) HH:mm:ss'
              )
            : ''}
        </FormElement>
      </FormListItem>
    );
  };

  renderUpdateUserField = () => {
    return (
      <FormListItem label='最終更新者'>
        <FormElement>{this.state.formData.update_user_name}</FormElement>
      </FormListItem>
    );
  };

  renderContentUpdateTimeField = () => {
    return (
      <FormListItem label='最終更新日時'>
        <FormElement>
          {this.state.formData.content_update_time !== null &&
          this.state.formData.update_user_name
            ? Moment(this.state.formData.content_update_time).format(
                'YYYY/MM/DD(ddd) HH:mm:ss'
              )
            : ''}
        </FormElement>
      </FormListItem>
    );
  };

  render() {
    return (
      <div
        className={`SafetyDiaries__items ${
          this.props.isSpace ? 'SafetyDiaries__items--space' : ''
        }`}
      >
        <div className='SafetyDiaries__items-left'>
          {/* 画像 */}
          {this.renderPatrolImageField()}
        </div>
        <div className='SafetyDiaries__items-right'>
          <FormList>
            {/* 記入日 */}
            {this.renderReportDateField()}
            {/* 記入者 */}
            {this.renderReporterField()}
            {/* 種別 */}
            {this.renderReportTypeField()}
            {/* スキーマ(定義)に応じたコントロールの表示 */}
            {_.map(
              this.schemaByReportType(this.state.formData.type),
              (field, index) => {
                if (
                  [
                    fieldNames.FIELD_A_ITEM,
                    fieldNames.FIELD_B_ITEM,
                    fieldNames.FIELD_C_ITEM,
                    fieldNames.FIELD_D_ITEM,
                  ].includes(field)
                ) {
                  return;
                }
                return (
                  <React.Fragment key={`form-list__item-${index}`}>
                    {this.renderField(field)}
                  </React.Fragment>
                );
              }
            )}
            {!_.isNull(this.props.dataId) && !this.state.isBulk && (
              <div>
                {this.renderCreateUserField()}
                {this.renderCreateTimeField()}
                {this.renderUpdateUserField()}
                {this.renderContentUpdateTimeField()}
              </div>
            )}
          </FormList>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/forms/SafetyDiaryForm.jsx